import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import Spinner from "@/common/components/Spinner";
import { useCompanyBranch } from "@/common/hooks/queries/useCompanyBranch";
import { useCompanyBranches } from "@/common/hooks/queries/useCompanyBranches";
import { Mapper } from "@/common/services/mapper";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { CompanyBranchData } from "@/features/company/types/company.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from '@tanstack/react-query';
import { BriefcaseBusinessIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { BranchInformation } from "../../components/form/BranchInformation";
import { ConsumptionRestriction } from "../../components/form/ConsumptionRestriction";
import { CreditConfiguration } from "../../components/form/CreditConfiguration";
import { GeneralInformation } from "../../components/form/GeneralInformation";
import { MasterClientUser } from "../../components/form/MasterClientUser";
import { PeriodRestriction } from "../../components/form/PeriodRestriction";
import useUpdateCompanyBranch from "../../hooks/useUpdateCompanyBranch";
import { companyBranchQuantitySchema, companyBranchSchema, CompanyBranchValidationSchema } from "../../schemas/companyBranch.schema";
import { CompanyBranchesTransformer } from "../../services/companyBranchesTransformer";
import { companyBranchDefaultValues } from "../../utils/companyBranchDefaultValues";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";

const CompanyBranchesUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [file, setFile] = useState<File>();

  const { data: companyData } = useMineCompany();

  const { data: branchData, isFetched: branchDataIsFetched } = useCompanyBranch(id || '');

  const { data: companyBranchOptions, isPending: companyBranchOptionsIsPending } = useCompanyBranches({
    filters: { pageSize: 1000 },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const { mutate: updateCompanyBranch, isPending } = useUpdateCompanyBranch({
    branchId: id,
    branchData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyBranch'] });
      navigate(`/company-branches/${id}`);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: CompanyBranchesTransformer.errorsTransformer()
      })(error);
    },
  });

  const schema = companyData?.current_contract.consumption_controlled_by === CompanyContractConsumptionControlledBy.Quantity ? companyBranchQuantitySchema : companyBranchSchema

  const formMethods = useForm<CompanyBranchValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: companyBranchDefaultValues({
      branchData,
      companyBranchOptions,
      controlledBy: companyData?.current_contract.consumption_controlled_by,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    watch,
    setValue,
    setError,
    reset,
    getValues,
    formState: { errors }
  } = formMethods;

  const onSubmit: SubmitHandler<CompanyBranchValidationSchema> = (data) => {
    updateCompanyBranch({ form: data, file });
  };

  useEffect(() => {
    if (branchDataIsFetched && !companyBranchOptionsIsPending) {
      reset(companyBranchDefaultValues({ branchData, companyBranchOptions, controlledBy: companyData?.current_contract.consumption_controlled_by }) );
    }
  }, [branchDataIsFetched, companyBranchOptionsIsPending]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          backTo="/company-branches"
          LogoPlaceholder={
            <ImageWithPlaceholder
              className="size-8"
              iconClassName="size-6"
              placeholderIcon={BriefcaseBusinessIcon}
              url={branchData?.general_information?.company_logo?.url}
              alt={branchData?.general_information?.fantasy_name}
            />
          }
          title={branchData?.display_name}
          buttons={(
            <Button
              name="submit"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" >
              {isPending ? (
                <Spinner className="h-6 mx-[23px] fill-white-800" />
              ) : (
                'Confirmar'
              )}
            </Button>
          )}
        />

        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">
            Dados da filial
          </p>

          <BranchInformation
            branchId={id}
            register={register}
            errors={errors}
            control={control}
            parentId={branchData?.parent_id}
            disableFields={companyData?.kind !== 'private_company'}
            showParentIdField={!!branchData?.parent_id}
          />

          <GeneralInformation
            reset={reset}
            getValues={getValues}
            clearErrors={clearErrors}
            companyData={companyData}
            branchData={branchData}
            register={register}
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            setError={setError}
            file={file}
            setFile={setFile}
            companyLogo={branchData?.general_information?.company_logo}
            disableCnpj={!branchData?.parent_id}
            disableFields={companyData?.kind !== 'private_company'}
          />

          <MasterClientUser
            errors={errors}
            control={control}
            disableFields={companyData?.kind !== 'private_company'}
          />

          <CreditConfiguration
            companyData={companyData}
            branchData={branchData}
            register={register}
            control={control}
            watch={watch}
            errors={errors}
            disableFields={companyData?.kind !== 'private_company'}
            companyKind={companyData?.kind}
          />

          <ConsumptionRestriction disabled={isPending} />

          <PeriodRestriction
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            disabled={isPending}
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </FormProvider>

  );
};

export default CompanyBranchesUpdate;
